import {
  BUILDINGS,
  BUILDING_IDS,
  CURRENCY,
  ECONOMY,
  FACTIONS,
  FACTION_ECONOMY,
  MARKET,
  TRADE_GOODS,
  TRADE_GOOD_IDS,
  type BuildingId,
  type FactionId,
  type SlotLayer,
  type TradeGoodId,
} from '@sd/content';
import {
  buildBlocker,
  buildCost,
  populationCap,
  quote,
  techMods,
  toLocal,
  upgradeBlocker,
  type Command,
  type GameState,
  type PlayerState,
} from '@sd/sim';
import { canBuy } from './fleets';
import type { Bot } from './match';

/**
 * Phase 1 planner: runs a capital the way a sensible new player would.
 * Keeps a few cycles of everything the planet consumes, sells what piles
 * up, and grows the planet along a faction build order. Bot strategy, not
 * game balance, so its thresholds live here rather than in content.
 */

/** How a planner plays. The three phase-1 strategies (issue #6) are presets of this. */
export interface PlannerStyle {
  /** Cycles of consumption kept in stock. */
  readonly reserveCycles: number;
  /** Cash never spent on construction, in ГС. */
  readonly cashReserve: number;
  /** Won't buy above this multiple of the base price. */
  readonly maxBuyRatio: number;
  /** Sells surplus only at or above this multiple of the base price. */
  readonly minSellRatio: number;
  /**
   * Speculation: buys any good below `buyBelow` × base with up to `budget`
   * of its cash and sells holdings above `sellAbove` × base. Null = never.
   */
  readonly speculate: {
    readonly buyBelow: number;
    readonly sellAbove: number;
    readonly budget: number;
  } | null;
  /**
   * Emission: prints what the next project or purchase lacks, plus the cash
   * reserve, while its currency is worth at least `minRate`. Null = never.
   */
  readonly print: { readonly minRate: number } | null;
  /** Buildings raised to level 2 before the faction's build order (personalities, #31). */
  readonly priority?: readonly BuildingId[];
  /** While true, the planner builds nothing: the treasury is saved for something bigger. */
  readonly hold?: (state: GameState, me: PlayerState) => boolean;
  /** Short of money for food, sells its biggest surplus at any price (phase-2 personalities). */
  readonly emergencySell?: boolean;
  /** Goods never sold as surplus: kept for something bigger. */
  readonly keep?: (state: GameState, me: PlayerState) => Partial<Record<TradeGoodId, number>>;
}

/** A sensible new player: the default for balance runs. */
export const BALANCED: PlannerStyle = {
  reserveCycles: 3,
  cashReserve: 150,
  maxBuyRatio: 2,
  minSellRatio: 0.9,
  speculate: null,
  print: null,
};

const BUILD_ORDER: Readonly<Record<FactionId, readonly BuildingId[]>> = {
  earthborn: ['miningComplex', 'smelter', 'electronicsFactory', 'commerceCenter', 'researchCenter'],
  reforged: ['agroComplex', 'electronicsFactory', 'commerceCenter', 'smelter', 'researchCenter'],
  drifters: ['agroComplex', 'miningComplex', 'commerceCenter', 'researchCenter'],
  skyborn: ['agroComplex', 'miningComplex', 'commerceCenter', 'researchCenter'],
};

/**
 * Building each faction pushes to the top level once its build order is done:
 * Earthborn go for the commerce center (the economic path and the reserve
 * currency, GDD §4, §6).
 */
const FOCUS: Readonly<Record<FactionId, BuildingId | null>> = {
  earthborn: 'commerceCenter',
  reforged: null,
  drifters: null,
  skyborn: null,
};

const consumption = new WeakMap<PlayerState['planet'], Record<TradeGoodId, number>>();

/** Units per cycle the planet uses of each good, from its population and buildings. */
export function consumptionPerCycle(player: PlayerState): Readonly<Record<TradeGoodId, number>> {
  // Many bots ask every tick; a planet that has not changed uses what it used (#50).
  const known = consumption.get(player.planet);
  if (known) return known;
  const { planet } = player;
  const use = Object.fromEntries(TRADE_GOOD_IDS.map((g) => [g, 0])) as Record<TradeGoodId, number>;
  use.food = planet.population * ECONOMY.foodPerPerson * FACTION_ECONOMY[player.faction].foodFactor;
  use.electronics = planet.population * ECONOMY.electronicsPerPerson;
  for (const b of planet.buildings) {
    for (const [good, amount] of Object.entries(BUILDINGS[b.id].inputs) as [
      TradeGoodId,
      number,
    ][]) {
      use[good] += amount * b.level * ECONOMY.satisfactionOutput.max;
    }
  }
  use.helium3 += planet.lastCycle?.consumed.helium3 ?? 0;
  consumption.set(planet, use);
  return use;
}

const ratio = (price: number, good: TradeGoodId) => price / TRADE_GOODS[good].basePrice;

function shoppingList(player: PlayerState, style: PlannerStyle): [TradeGoodId, number][] {
  const use = consumptionPerCycle(player);
  return TRADE_GOOD_IDS.map((g): [TradeGoodId, number] => [
    g,
    Math.ceil(use[g] * style.reserveCycles - player.goods[g]),
  ]).filter(([, missing]) => missing > 0);
}

/** The next construction the planner wants, or null. */
function nextProject(
  player: PlayerState,
  style: PlannerStyle,
  closed: ReadonlySet<TradeGoodId>,
): { building: BuildingId; layer: SlotLayer } | { index: number } | null {
  const { planet } = player;
  const report = planet.lastCycle;
  // Technologies add slots (orbit, and the surface since #79): the planner sees them too.
  const mods = techMods(player.techs);
  // The surface is scarcer than the orbit: a building that fits either goes up there
  // first (#125: a second power plant took the trader's last surface slot, and its
  // research center never found one).
  const tryBuild = (building: BuildingId) => {
    const layers = [...BUILDINGS[building].layers].sort(
      (a, b) => (a === 'orbit' ? -1 : 0) - (b === 'orbit' ? -1 : 0),
    );
    for (const layer of layers) {
      if (!buildBlocker(planet, building, layer, mods)) return { building, layer };
    }
    return null;
  };
  const tryUpgrade = (building: BuildingId) => {
    const index = planet.buildings.findIndex(
      (b) => b.id === building && !upgradeBlocker(planet, planet.buildings.indexOf(b)),
    );
    return index >= 0 ? { index } : null;
  };
  const either = (building: BuildingId) => tryBuild(building) ?? tryUpgrade(building);

  if (planet.buildings.some((b) => b.construction)) return null;
  // Short of energy: a higher level first, so a new plant does not take a slot a
  // research center needs (#107: the trader never had one free all match).
  if (report && report.energyRatio < 1) return tryUpgrade('powerPlant') ?? tryBuild('powerPlant');
  // A good the exchange will not sell (an embargo, #64) is made at home instead.
  for (const good of closed) {
    const short = consumptionPerCycle(player)[good] > (report?.produced[good] ?? 0);
    const maker = BUILDING_IDS.find((b) => (BUILDINGS[b].outputs[good] ?? 0) > 0);
    const project = short && maker ? either(maker) : null;
    if (project) return project;
  }
  if (planet.population >= populationCap(planet) - 1) {
    const housing = tryUpgrade('housing') ?? tryBuild('housing');
    if (housing) return housing;
  }
  for (const building of style.priority ?? []) {
    const have = planet.buildings.find((b) => b.id === building);
    const project = !have ? tryBuild(building) : have.level < 2 ? tryUpgrade(building) : null;
    if (project) return project;
  }
  for (const building of BUILD_ORDER[player.faction]) {
    if (!planet.buildings.some((b) => b.id === building)) {
      const project = tryBuild(building);
      if (project) return project;
    }
  }
  // Everything planned stands: first the faction's focus, then whatever is cheapest to grow.
  const focus = FOCUS[player.faction];
  if (focus) {
    const project = tryUpgrade(focus);
    if (project) return project;
  }
  const upgradable = planet.buildings
    .map((b, index) => ({ b, index }))
    .filter(({ b, index }) => BUILDINGS[b.id].jobs > 0 && !upgradeBlocker(planet, index))
    .sort((x, y) => x.b.level - y.b.level);
  return upgradable[0] ? { index: upgradable[0].index } : null;
}

/** Emission that covers `missingLocal`, or null when the style does not print or the rate is too low. */
function printFor(me: PlayerState, style: PlannerStyle, missingLocal: number): Command | null {
  if (!style.print || me.currency.rate < style.print.minRate || missingLocal <= 0) return null;
  const amount = Math.min(CURRENCY.maxEmission, Math.ceil(missingLocal));
  return { type: 'emit', playerId: me.id, amount };
}

/** Alloys a constructor and its station take: never sold as surplus. */
const SETTLER_ALLOYS = 30;

export function makePlanner(style: PlannerStyle): Bot {
  return (state, playerId, rng) => {
    if (state.pause || !rng.chance(0.03)) return [];
    const me = state.players.find((p) => p.id === playerId);
    if (!me) return [];

    // Planning happens in ГС: what our cash is worth on the exchange, after conversion.
    const rate = me.currency.rate;
    const toGs = (local: number) => (local * rate) / (1 + CURRENCY.conversionFee);
    const toCash = (gs: number) => (gs / rate) * (1 + CURRENCY.conversionFee);
    const gsCash = toGs(me.cash);

    // 1. Never run short of what the planet eats and burns.
    const closed = new Set(TRADE_GOOD_IDS.filter((g) => !canBuy(state, playerId, g)));
    for (const [good, missing] of shoppingList(me, style)) {
      if (closed.has(good)) continue;
      const price = state.market[good].price;
      if (ratio(price, good) > style.maxBuyRatio) continue;
      const unit = price * (1 + MARKET.fee) * 1.2;
      const wanted = Math.min(missing, MARKET.maxOrderUnits);
      const affordable = Math.floor(gsCash / unit);
      const quantity = Math.min(wanted, affordable);
      if (quantity < wanted) {
        const emission = printFor(me, style, toCash((wanted - quantity) * unit));
        if (emission) return [emission];
        // Short of money for what the planet eats: sell the biggest surplus at any price.
        const use = consumptionPerCycle(me);
        const spare = TRADE_GOOD_IDS.filter((g) => g !== good)
          .map((g) => ({ g, n: Math.floor(me.goods[g] - use[g] * style.reserveCycles) }))
          .filter((x) => x.n >= 1)
          .sort((a, b) => b.n * state.market[b.g].price - a.n * state.market[a.g].price)[0];
        if (style.emergencySell && spare && quantity < 1) {
          const n = Math.min(spare.n, MARKET.maxOrderUnits);
          return [{ type: 'marketSell', playerId, good: spare.g, quantity: n }];
        }
      }
      if (quantity >= 1) return [{ type: 'marketBuy', playerId, good, quantity }];
    }

    // 2. Grow the planet, unless the treasury is kept for something bigger.
    const project = style.hold?.(state, me) ? null : nextProject(me, style, closed);
    if (project) {
      const building =
        'index' in project ? me.planet.buildings[project.index]!.id : project.building;
      const level = 'index' in project ? me.planet.buildings[project.index]!.level + 1 : 1;
      const cost = buildCost(building, level);
      const commands: Command[] = [];
      let cash = me.cash;
      for (const good of ['alloys', 'metals'] as const) {
        const missing = Math.ceil(cost[good] - me.goods[good]);
        if (missing > 0 && !closed.has(good)) {
          const q = quote(state.market, good, 'buy', Math.min(missing, MARKET.maxOrderUnits));
          cash -= toCash(q.total);
          commands.push({ type: 'marketBuy', playerId, good, quantity: q.quantity });
        }
      }
      const reserve = toLocal(style.cashReserve, rate);
      const left = cash - toLocal(cost.cash, rate);
      if (left >= reserve) {
        commands.push(
          'index' in project
            ? { type: 'upgrade', playerId, index: project.index }
            : { type: 'build', playerId, building: project.building, layer: project.layer },
        );
        return commands;
      }
      const emission = printFor(me, style, reserve - left);
      if (emission) return [emission];
    }

    // 3. Sell what piles up beyond twice the reserve.
    const use = consumptionPerCycle(me);
    const kept = style.keep?.(state, me) ?? {};
    // What the next building takes stays (#125: alloys bought for it were sold back).
    const building = project
      ? buildCost(
          'index' in project ? me.planet.buildings[project.index]!.id : project.building,
          'index' in project ? me.planet.buildings[project.index]!.level + 1 : 1,
        )
      : null;
    // ...and a constructor with its station's alloys, which the settler buys ahead.
    const forBuilding = (good: TradeGoodId) =>
      (building && (good === 'alloys' || good === 'metals') ? building[good] : 0) +
      (good === 'alloys' ? SETTLER_ALLOYS : 0);
    for (const good of TRADE_GOOD_IDS) {
      const surplus = Math.floor(
        me.goods[good] -
          use[good] * style.reserveCycles * 2 -
          10 -
          (kept[good] ?? 0) -
          forBuilding(good),
      );
      if (surplus >= 1 && ratio(state.market[good].price, good) >= style.minSellRatio) {
        const quantity = Math.min(surplus, MARKET.maxOrderUnits);
        return [{ type: 'marketSell', playerId, good, quantity }];
      }
    }

    // 4. Speculate on price swings.
    if (style.speculate) {
      const { buyBelow, sellAbove, budget } = style.speculate;
      const good = rng.pick(TRADE_GOOD_IDS);
      const r = ratio(state.market[good].price, good);
      const spare = Math.floor(me.goods[good] - use[good] * style.reserveCycles);
      // Never buy what step 3 would sell straight back (#107): that churn paid the fee
      // twice and only pumped the turnover. The faction's own cheap good is never bought.
      const room = Math.floor(
        use[good] * style.reserveCycles * 2 + 10 + (kept[good] ?? 0) - me.goods[good],
      );
      if (r >= sellAbove && spare >= 1) {
        return [
          { type: 'marketSell', playerId, good, quantity: Math.min(spare, MARKET.maxOrderUnits) },
        ];
      }
      if (
        r <= buyBelow &&
        !closed.has(good) &&
        good !== FACTIONS[me.faction].cheapGood &&
        room >= 1
      ) {
        const unit = state.market[good].price * (1 + MARKET.fee) * 1.1;
        const quantity = Math.min(Math.floor((gsCash * budget) / unit), MARKET.maxOrderUnits, room);
        if (quantity >= 1) return [{ type: 'marketBuy', playerId, good, quantity }];
      }
    }
    return [];
  };
}

export const plannerBot: Bot = makePlanner(BALANCED);
